import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Col,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
// @ts-expect-error
import translations from "ckeditor5/translations/ko.js";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  BlockToolbar,
  Bold,
  Bookmark,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageEditing,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUtils,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Markdown,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromMarkdownExperimental,
  PasteFromOffice,
  RemoveFormat,
  ShowBlocks,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
} from "ckeditor5";
import DropdownStatusNew from "./DropdownStatusNew";
/**
 * Create a free account with a trial: https://portal.ckeditor.com/checkout?plan=free
 */
const LICENSE_KEY = "GPL";

interface ModalEditContentProps {
  header?: string;
  title?: string;
  content?: string | React.ReactNode;
  isShowIcon?: boolean;
  textButtonClose?: string;
  isShowButtonClose?: boolean;
  textButtonConfirm?: string;
  classButtonConfirm?: string;
  classIconButtonConfirm?: string;
  isShowButtonConfirm?: boolean;
  isView?: boolean;
  isOpen: any;
  onClose?: () => void;
  onConfirm?: () => void;
}

const ModalEditContent = ({
  header,
  isOpen = null,
  onClose,
  onConfirm,
  isView,
}: ModalEditContentProps) => {
  const { t, i18n } = useTranslation();
  const editorContainerRef = useRef(null);
  const editorRef = useRef<any>(isOpen?.value || "");

  const [isLayoutReady, setIsLayoutReady] = useState(false);

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const { editorConfig }: any = useMemo(() => {
    if (!isLayoutReady) {
      return {};
    }

    return {
      editorConfig: {
        toolbar: {
          items: [
            "sourceEditing",
            // "showBlocks",
            // "findAndReplace",
            "|",
            "heading",
            // "style",
            "|",
            "fontSize",
            "fontFamily",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            // "subscript",
            // "superscript",
            // "code",
            // "removeFormat",
            // "|",
            // "specialCharacters",
            // "horizontalLine",
            // "pageBreak",
            // "link",
            // "bookmark",
            // "insertImageViaUrl",
            // "mediaEmbed",
            // "insertTable",
            // "highlight",
            // "blockQuote",
            // "codeBlock",
            // "htmlEmbed",
            // "|",
            // "alignment",
            // "|",
            // "bulletedList",
            // "numberedList",
            // "todoList",
            // "outdent",
            // "indent",
          ],
          shouldNotGroupWhenFull: false,
        },
        plugins: [
          Alignment,
          Autoformat,
          AutoImage,
          AutoLink,
          Autosave,
          BalloonToolbar,
          BlockQuote,
          BlockToolbar,
          Bold,
          Bookmark,
          Code,
          CodeBlock,
          Essentials,
          FindAndReplace,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          FullPage,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HorizontalLine,
          HtmlComment,
          HtmlEmbed,
          ImageBlock,
          ImageCaption,
          ImageEditing,
          ImageInline,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUtils,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          Markdown,
          MediaEmbed,
          PageBreak,
          Paragraph,
          PasteFromMarkdownExperimental,
          PasteFromOffice,
          RemoveFormat,
          ShowBlocks,
          SourceEditing,
          SpecialCharacters,
          SpecialCharactersArrows,
          SpecialCharactersCurrency,
          SpecialCharactersEssentials,
          SpecialCharactersLatin,
          SpecialCharactersMathematical,
          SpecialCharactersText,
          Strikethrough,
          Style,
          Subscript,
          Superscript,
          // Table,
          // TableCaption,
          // TableCellProperties,
          // TableColumnResize,
          // TableProperties,
          // TableToolbar,
          TextTransformation,
          TodoList,
          Underline,
        ],
        balloonToolbar: [
          "bold",
          "italic",
          "|",
          "link",
          "|",
          "bulletedList",
          "numberedList",
        ],
        blockToolbar: [
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "|",
          "bold",
          "italic",
          "|",
          "link",
          "insertTable",
          "|",
          "bulletedList",
          "numberedList",
          "outdent",
          "indent",
        ],
        fontFamily: {
          supportAllValues: true,
        },
        fontSize: {
          options: [10, 12, 14, "default", 18, 20, 22],
          supportAllValues: true,
        },
        heading: {
          options: [
            {
              model: "paragraph",
              title: "Paragraph",
              class: "ck-heading_paragraph",
            },
            {
              model: "heading1",
              view: "h1",
              title: "Heading 1",
              class: "ck-heading_heading1",
            },
            {
              model: "heading2",
              view: "h2",
              title: "Heading 2",
              class: "ck-heading_heading2",
            },
            {
              model: "heading3",
              view: "h3",
              title: "Heading 3",
              class: "ck-heading_heading3",
            },
            {
              model: "heading4",
              view: "h4",
              title: "Heading 4",
              class: "ck-heading_heading4",
            },
            {
              model: "heading5",
              view: "h5",
              title: "Heading 5",
              class: "ck-heading_heading5",
            },
            {
              model: "heading6",
              view: "h6",
              title: "Heading 6",
              class: "ck-heading_heading6",
            },
          ],
        },
        htmlSupport: {
          allow: [
            {
              name: /^.*$/,
              styles: true,
              attributes: true,
              classes: true,
            },
          ],
        },
        image: {
          toolbar: [
            "toggleImageCaption",
            "imageTextAlternative",
            "|",
            "imageStyle:inline",
            "imageStyle:wrapText",
            "imageStyle:breakText",
            "|",
            "resizeImage",
          ],
        },
        initialData: isOpen?.value,
        language: "ko",
        licenseKey: LICENSE_KEY,
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: "https://",
          decorators: {
            toggleDownloadable: {
              mode: "manual",
              label: "Downloadable",
              attributes: {
                download: "file",
              },
            },
          },
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true,
          },
        },
        placeholder: t("Type or paste your content here"),
        style: {
          definitions: [
            {
              name: "Article category",
              element: "h3",
              classes: ["category"],
            },
            {
              name: "Title",
              element: "h2",
              classes: ["document-title"],
            },
            {
              name: "Subtitle",
              element: "h3",
              classes: ["document-subtitle"],
            },
            {
              name: "Info box",
              element: "p",
              classes: ["info-box"],
            },
            {
              name: "Side quote",
              element: "blockquote",
              classes: ["side-quote"],
            },
            {
              name: "Marker",
              element: "span",
              classes: ["marker"],
            },
            {
              name: "Spoiler",
              element: "span",
              classes: ["spoiler"],
            },
            {
              name: "Code (dark)",
              element: "pre",
              classes: ["fancy-code", "fancy-code-dark"],
            },
            {
              name: "Code (bright)",
              element: "pre",
              classes: ["fancy-code", "fancy-code-bright"],
            },
          ],
        },
        translations: [i18n?.language !== "en" ? translations : ""],
        // table: {
        //   contentToolbar: [
        //     "tableColumn",
        //     "tableRow",
        //     "mergeTableCells",
        //     "tableProperties",
        //     "tableCellProperties",
        //   ],
        // },
      },
    };
  }, [isLayoutReady]);

  const [content, setContent] = useState("");
  const [devices, setDevices] = useState("devices_mo");

  const handleCloseConfirmClick = () => {
    editorRef.current
      .destroy()
      .then(() => {
        editorRef.current = null;
      })
      .catch((error: any) => console.error("Destroy error:", error));
    onClose && onClose();
  };

  useEffect(() => {
    setContent(isOpen?.value || "");
  }, [isOpen?.value]);

  const handleActionConfirm = () => {
    if (editorRef?.current?.getData() === "<p>&nbsp;</p>") {
      return;
    }
    editorRef.current
      .destroy()
      .then(() => {
        editorRef.current = null;
      })
      .catch((error: any) => console.error("Destroy error:", error));
    !!isOpen?.cb && isOpen.cb(editorRef?.current?.getData());
    onConfirm && onConfirm();
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={!!isOpen}
        centered={true}
        style={{
          width: isOpen?.width || "max-content",
          maxWidth: "100%",
          height: isOpen?.height || "80%",
        }}
        toggle={handleCloseConfirmClick}
        contentClassName="h-100"
        keyboard={true}
      >
        <ModalHeader toggle={handleCloseConfirmClick}>{header}</ModalHeader>
        <ModalBody style={{ height: "calc(100% - 50px)" }}>
          <div className="d-flex flex-column g-4 w-100 h-100">
            <Row style={{ height: "calc(100% - 50px)" }}>
              <Col sm={isView ? 6 : 12} className="h-100">
                <div className="h-100">
                  <div className="text-center h-100">
                    <div
                      className="editor-container editor-container_classic-editor editor-container_include-style editor-container_include-block-toolbar h-100"
                      ref={editorContainerRef}
                    >
                      <div
                        className="editor-container__editor"
                        style={{
                          maxWidth: isOpen?.width,
                          width: "100%",
                          // maxHeight: isOpen?.height,
                          height: "100%",
                          // overflow: "auto",
                          // borderBottom: "1px solid hsl(220, 6%, 81%)"
                        }}
                      >
                        <div ref={editorRef} className="h-100">
                          {editorConfig && (
                            <CKEditor
                              editor={ClassicEditor}
                              config={editorConfig}
                              data={isOpen?.value || ""}
                              onChange={(event, editor) => {
                                const data = editor.getData();
                                setContent(data); // Cập nhật state để preview
                              }}
                              onReady={(editor) => {
                                editorRef.current = editor;
                              }}
                              // onChange={() => {
                              //   console.log(
                              //     "editorRef.current?.getData()",
                              //     editorRef?.current?.getData()
                              //   );
                              // }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              {isView && (
                <Col sm={6} className="d-flex flex-column h-100">
                  <DropdownStatusNew
                    name="category"
                    dataList={[
                      {
                        label: "Mobile",
                        value: "devices_mo",
                      },
                      {
                        label: "Fold",
                        value: "devices_fold",
                      },
                    ]}
                    placeholder={`${t("Select category")}...`}
                    className="dropdown-status-rounded"
                    classNamePrefix="name-prefix"
                    initialValue={devices || null}
                    onChangeSelect={(event: any) => {
                      setDevices(event?.value);
                    }}
                  />
                  <div
                    className={`${devices} border border-black mt-2 mx-auto p-3 overflow-auto h-100`}
                    style={{ maxHeight: "calc(100% - 46px)" }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: content || "",
                      }}
                    ></div>
                  </div>
                </Col>
              )}
            </Row>
            <div className="hstack gap-2 justify-content-center mt-4">
              <button
                className="btn btn-soft-secondary fs-14"
                type="button"
                color="light"
                onClick={handleCloseConfirmClick}
              >
                {t("Button Close")}
              </button>

              <button
                className={`btn btn-primary fs-14`}
                type="button"
                onClick={handleActionConfirm}
              >
                Submit
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ModalEditContent;
